import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, Target, ShoppingCart, DollarSign, BarChart3 } from 'lucide-react';
import { formatCurrencyCompact, formatPercentChange } from '../utils/formatters';

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.6 } },
};
const cardVar = {
  hidden: { opacity: 0, x: 40 },
  show: { opacity: 1, x: 0, transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] } },
};

export function InsightsRibbon({ trendPercentages, comparisonAggregates, googleAdsSummary, metaAdsSummary, funnelSummary }) {
  const insights = useMemo(() => {
    const items = [];
    const tp = trendPercentages || {};
    const cur = comparisonAggregates?.cur || {};

    // Revenue insight
    if (cur.totalRevenue) {
      items.push({
        title: 'Revenue',
        value: formatCurrencyCompact(cur.totalRevenue),
        change: tp.totalRevenue,
        sentiment: (tp.totalRevenue || 0) >= 0 ? 'positive' : 'negative',
        icon: DollarSign,
        detail: `${formatPercentChange(tp.totalRevenue)} vs previous period`,
      });
    }

    // Sessions insight
    if (cur.sessions) {
      items.push({
        title: 'Sessions',
        value: cur.sessions.toLocaleString(),
        change: tp.sessions,
        sentiment: (tp.sessions || 0) >= 0 ? 'positive' : 'negative',
        icon: BarChart3,
        detail: `${formatPercentChange(tp.sessions)} vs previous period`,
      });
    }

    // Conversion Rate
    if (comparisonAggregates?.curCR) {
      items.push({
        title: 'Conv. Rate',
        value: `${comparisonAggregates.curCR.toFixed(2)}%`,
        change: tp.conversionRate,
        sentiment: (tp.conversionRate || 0) >= 0 ? 'positive' : 'negative',
        icon: Target,
        detail: `${formatPercentChange(tp.conversionRate)} vs previous`,
      });
    }

    // Google Ads ROAS
    if (googleAdsSummary?.roas > 0) {
      items.push({
        title: 'G.Ads ROAS',
        value: `${googleAdsSummary.roas.toFixed(2)}x`,
        change: null,
        sentiment: googleAdsSummary.roas >= 2 ? 'positive' : googleAdsSummary.roas >= 1 ? 'neutral' : 'negative',
        icon: TrendingUp,
        detail: `Spend: ${formatCurrencyCompact(googleAdsSummary.totalCost)}`,
      });
    }

    // Meta Ads ROAS
    if (metaAdsSummary?.roas > 0) {
      items.push({
        title: 'Meta ROAS',
        value: `${metaAdsSummary.roas.toFixed(2)}x`,
        change: null,
        sentiment: metaAdsSummary.roas >= 2 ? 'positive' : metaAdsSummary.roas >= 1 ? 'neutral' : 'negative',
        icon: TrendingUp,
        detail: `Spend: ${formatCurrencyCompact(metaAdsSummary.totalSpend)}`,
      });
    }

    // Funnel
    if (funnelSummary?.conversionRate > 0) {
      items.push({
        title: 'Funnel CR',
        value: `${funnelSummary.conversionRate}%`,
        change: null,
        sentiment: funnelSummary.conversionRate >= 3 ? 'positive' : 'neutral',
        icon: ShoppingCart,
        detail: `Drop-off: ${funnelSummary.dropoffRate}%`,
      });
    }

    return items;
  }, [trendPercentages, comparisonAggregates, googleAdsSummary, metaAdsSummary, funnelSummary]);

  if (insights.length === 0) return null;

  const borderColor = { positive: 'border-emerald-400', negative: 'border-red-400', neutral: 'border-amber-400' };
  const iconBg = { positive: 'bg-emerald-50 text-emerald-600', negative: 'bg-red-50 text-red-600', neutral: 'bg-amber-50 text-amber-600' };

  return (
    <div className="mb-6 relative">
      {/* Right fade */}
      <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-[#f6f8ff] to-transparent z-10 pointer-events-none" />

      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {insights.map((insight, i) => {
          const Icon = insight.icon;
          return (
            <motion.div
              key={i}
              variants={cardVar}
              className={`w-64 shrink-0 glass-card rounded-xl p-4 border-l-4 ${borderColor[insight.sentiment]} hover-lift`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${iconBg[insight.sentiment]}`}>
                  <Icon size={14} />
                </div>
                {insight.change != null && (
                  <span className={insight.change >= 0 ? 'metric-badge-up' : 'metric-badge-down'}>
                    {insight.change >= 0 ? '+' : ''}{Number(insight.change).toFixed(1)}%
                  </span>
                )}
              </div>
              <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">{insight.title}</p>
              <p className="text-xl font-bold text-gray-900 mt-0.5">{insight.value}</p>
              <p className="text-[11px] text-gray-400 mt-1">{insight.detail}</p>
            </motion.div>
          );
        })}
      </motion.div>
    </div>
  );
}
